import otelloConstants from 'dashboard/constants/globals';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { emitter } from 'shared/helpers/mitt';

import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import {
  WHATSAPP_CONVERSATION_CHANNEL_TYPE,
  cloneConversationCacheList,
  clonePlainValue,
} from '../../../helper/conversationCache';
import { areSameConversationId } from '../../../helper/conversationIdentity';
import {
  AI_NEEDS_HUMAN_ATTRIBUTE_KEY,
  AI_SCHEDULED_STATE_ATTRIBUTE_KEY,
  AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY,
  isStaleConversationAiScheduledStateSnapshot,
} from '../../../helper/conversationScheduledItems';
import {
  mergeConversationMessagesForDetailUpdate,
  mergeConversationMessagesForListUpdate,
} from '../../../helper/conversationMessageLoadState';
import {
  buildConversationUnreadPatch,
  buildConversationReadStateOperation,
  isConversationReadStateOperationActive,
  resolveConversationReadStateUpdate,
} from '../../../helper/conversationUnreadState';
import {
  buildConversationPlacementOperation,
  CONVERSATION_PLACEMENT_SOURCE,
  filterPendingPlacementConversations,
  isConversationPlacementOperationActive,
  resolveConversationPlacementUpdate,
  shouldFloatConversationOnRealtimeMessage,
  shouldPreserveConversationPageOrder,
} from '../../../helper/conversationPlacementState';
import {
  deferFrozenConversationFloat,
  freezeConversationPlacement,
  isConversationPlacementFrozen,
  pinFrozenConversationPlacement,
  releaseConversationPlacement,
  shouldFreezeConversationPlacement,
} from '../../../helper/conversationFrozenPlacement';
import {
  clearConversationFreshArrival,
  markConversationFreshArrival,
} from '../../../helper/conversationFreshArrival';
import types from '../../mutation-types';
import actions from './actions';
import getters, { getSelectedChatConversation } from './getters';
import { findPendingMessageIndex } from './helpers';

const state = {
  allConversations: [],
  listLoadingStatus: true,
  chatStatusFilter: otelloConstants.STATUS_TYPE.OPEN,
  chatSortFilter: otelloConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  activeInboxFilter: null,
  selectedChatId: null,
  selectedChat: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  totalConversations: 0,
  unreadCount: 0,
  unreadCountByInbox: {},
  paginationLoading: false,
  lastFetchedConversationId: null,
  hasNewConversations: false,
  // Cache universale per conversazioni di tutte le inbox
  conversationCache: {
    // Struttura: { 'cacheKey': { conversations: [], timestamp: Date, filters: {}, inboxType: 'email|chatbot|whatsapp...' } }
  }, // Configurazioni di scadenza cache per tipo di canale
  cacheExpirationByType: {
    // 📧 CANALI EMAIL - Tempi lunghi (simili a Gmail Web)
    'Channel::Email': 30 * 60 * 1000, // 30 minuti per email (Gmail, Outlook, etc.)

    // 🤖 CANALI CHATBOT/API - Tempi moderati
    'Channel::Api': 10 * 60 * 1000, // 10 minuti per API - generico

    // 💬 CANALI MESSAGGISTICA REAL-TIME - Tempi brevi
    [WHATSAPP_CONVERSATION_CHANNEL_TYPE]: 5 * 60 * 1000, // 5 minuti per WhatsApp
    'Channel::Telegram': 5 * 60 * 1000, // 5 minuti per Telegram

    // 📞 CANALI VOCE/VIDEO - Tempi brevi (critici)
    'Channel::Voice': 3 * 60 * 1000, // 3 minuti per chiamate vocali
    'Channel::VideoCall': 3 * 60 * 1000, // 3 minuti per videochiamate

    // ⚙️ DEFAULT - Tempo standard per canali non specificati
    default: 10 * 60 * 1000, // 10 minuti per canali non specificati
  },
  // Cache delle bozze completate per conversazioni non in memoria
  draftCompletionCache: {},
  pendingReadStateOperations: {},
  pendingPlacementOperations: {},
  // Conversazioni su cui Otello sta lavorando (typing su inbox API): la riga
  // tiene il posto finche' il typing e' acceso. Vedi conversationFrozenPlacement.
  frozenPlacements: {},
  // Conversazioni entrate in lista da un evento realtime (pillola "Nuovo"):
  // id -> epoch ms dell'ingresso. Vedi conversationFreshArrival.
  freshArrivals: {},
  // Unread-first toggle (persisted in uiSettings)
  unreadFirstEnabled: false,
  // Priority filter toggle (persisted in uiSettings)
  priorityFilterEnabled: false,
};

const CONVERSATION_NEW_MESSAGE_BADGE_ID_FIELD = 'recently_new_message_id';
const CONVERSATION_NEW_MESSAGE_BADGE_EXPIRES_AT_FIELD =
  'recently_new_message_expires_at';

const findConversationIndex = (conversations = [], conversationId = null) =>
  conversations.findIndex((conversation) =>
    areSameConversationId(conversation.id, conversationId)
  );

const findConversation = (conversations = [], conversationId = null) => {
  const index = findConversationIndex(conversations, conversationId);
  return index === -1 ? null : conversations[index];
};

const normalizeAttachmentId = (value) => (value == null ? null : String(value));

const normalizeMessageUpdatedAt = (value = null) => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue > 9999999999 ? numericValue : numericValue * 1000;
  }

  const parsedValue = Date.parse(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isOlderMessageSnapshot = (currentMessage = {}, incomingMessage = {}) => {
  const currentUpdatedAt = normalizeMessageUpdatedAt(currentMessage.updated_at);
  const incomingUpdatedAt = normalizeMessageUpdatedAt(
    incomingMessage.updated_at
  );

  return (
    currentUpdatedAt !== null &&
    incomingUpdatedAt !== null &&
    incomingUpdatedAt < currentUpdatedAt
  );
};

const ensureConversationAttachments = (conversation) => {
  if (!Array.isArray(conversation.attachments)) {
    conversation.attachments = [];
  }
  return conversation.attachments;
};

const getSelectedConversationSnapshot = (_state) => {
  if (!_state.selectedChatId) {
    return null;
  }

  const conversationInList = findConversation(
    _state.allConversations,
    _state.selectedChatId
  );
  if (conversationInList) {
    return conversationInList;
  }

  if (
    _state.selectedChat &&
    areSameConversationId(_state.selectedChat.id, _state.selectedChatId)
  ) {
    return _state.selectedChat;
  }

  return null;
};

const setSelectedConversationSnapshot = (_state, conversation = null) => {
  if (
    conversation?.id &&
    areSameConversationId(conversation.id, _state.selectedChatId)
  ) {
    _state.selectedChat = conversation;
  }
};

const getPendingReadStateOperation = (_state, conversationId = null) => {
  if (conversationId === null || conversationId === undefined) {
    return null;
  }

  const operation =
    _state.pendingReadStateOperations?.[String(conversationId)] || null;

  return isConversationReadStateOperationActive({ operation })
    ? operation
    : null;
};

const resolveConversationReadStateForStore = (
  _state,
  currentConversation = null,
  incomingConversation = null,
  options = {}
) =>
  resolveConversationReadStateUpdate({
    currentConversation,
    incomingConversation,
    messageCreatedAt: options.messageCreatedAt,
    pendingOperation: getPendingReadStateOperation(
      _state,
      incomingConversation?.id ?? currentConversation?.id
    ),
    source: options.source,
  });

const getPendingPlacementOperation = (_state, conversationId = null) => {
  if (conversationId === null || conversationId === undefined) {
    return null;
  }

  const operation =
    _state.pendingPlacementOperations?.[String(conversationId)] || null;

  return isConversationPlacementOperationActive({ operation })
    ? operation
    : null;
};

const preserveDraftGenerationState = (
  currentConversation = {},
  incomingConversation = {}
) => {
  const localFields = [
    'activeDraftGenerationIds',
    'completedDraftGenerationIds',
    'isGeneratingDraft',
  ].reduce((fields, field) => {
    if (Object.prototype.hasOwnProperty.call(currentConversation, field)) {
      fields[field] = currentConversation[field];
    }
    return fields;
  }, {});

  return { ...incomingConversation, ...localFields };
};

// Fusione di una riga di lista già presente con quella appena arrivata dal
// server: stato di lettura risolto, anteprima fusa e chat aperta protetta dal
// payload ridotto della lista. È il trattamento che SET_ALL_CONVERSATION
// applica a ogni fetch e che il delta del reconnect deve applicare uguale.
const mergeConversationForListUpdate = (_state, prev, incoming) => {
  const resolvedConversation = resolveConversationReadStateForStore(
    _state,
    prev,
    incoming
  );

  if (areSameConversationId(incoming.id, _state.selectedChatId)) {
    return preserveDraftGenerationState(prev, {
      ...resolvedConversation,
      allMessagesLoaded: prev.allMessagesLoaded,
      messages: prev.messages,
      dataFetched: prev.dataFetched,
      attachments: prev.attachments,
      lastMessageClassification: prev.lastMessageClassification,
    });
  }

  return mergeConversationMessagesForListUpdate(prev, resolvedConversation);
};

const resolveConversationPlacementForStore = (
  _state,
  currentConversation = null,
  incomingConversation = null,
  options = {}
) =>
  resolveConversationPlacementUpdate({
    chatSortFilter: _state.chatSortFilter,
    currentConversation,
    currentPage: _state.conversationFilters?.page,
    incomingConversation,
    pendingOperation: getPendingPlacementOperation(
      _state,
      incomingConversation?.id ?? currentConversation?.id
    ),
    source: options.source,
  });

// Con l'ordine delle pagine >1 congelato il getter non riordina: gli insert
// realtime vanno in testa, dove una lista latest-first li mostrerebbe.
// E' l'unico punto di ingresso in lista con sorgente REALTIME (ADD_CONVERSATION
// e il ramo di inserimento di UPDATE_CONVERSATION): qui si timbra l'arrivo
// per la pillola "Nuovo". Fetch, cache e paginazione non passano di qui.
const insertRealtimeConversation = (_state, conversation) => {
  const nextFreshArrivals = markConversationFreshArrival({
    conversationId: conversation?.id,
    freshArrivals: _state.freshArrivals,
  });
  if (nextFreshArrivals) {
    _state.freshArrivals = nextFreshArrivals;
  }
  if (
    shouldPreserveConversationPageOrder({
      currentPage: _state.conversationFilters?.page,
    })
  ) {
    _state.allConversations.unshift(conversation);
  } else {
    _state.allConversations.push(conversation);
  }
};

const floatConversationToTop = (_state, conversationId) => {
  const index = findConversationIndex(_state.allConversations, conversationId);
  if (index <= 0) return;

  const [conversation] = _state.allConversations.splice(index, 1);
  _state.allConversations.unshift(conversation);
};

// Otello al lavoro (typing su inbox API): la risalita e' rimandata allo
// sblocco, la riga tiene il posto.
const floatConversationToTopUnlessFrozen = (_state, conversationId) => {
  if (!isConversationPlacementFrozen(_state.frozenPlacements, conversationId)) {
    floatConversationToTop(_state, conversationId);
    return;
  }

  const nextFrozenPlacements = deferFrozenConversationFloat({
    conversationId,
    frozenPlacements: _state.frozenPlacements,
  });
  if (nextFrozenPlacements) {
    _state.frozenPlacements = nextFrozenPlacements;
  }
};

// Da chiamare PRIMA di scrivere i campi di ordinamento di una conversazione:
// se e' congelata e non ha ancora l'istantanea (entrata in lista dopo il
// typing_on), fissa i valori di adesso. Altrimenti non fa nulla.
const pinFrozenConversationPlacementForStore = (_state, conversation) => {
  const nextFrozenPlacements = pinFrozenConversationPlacement({
    conversation,
    frozenPlacements: _state.frozenPlacements,
  });
  if (nextFrozenPlacements) {
    _state.frozenPlacements = nextFrozenPlacements;
  }
};

const removeConversationsFromCache = (_state, conversationIds = []) => {
  const ids = Array.isArray(conversationIds)
    ? conversationIds
    : [conversationIds];
  if (!ids.length) return;

  Object.values(_state.conversationCache || {}).forEach((cache) => {
    if (!Array.isArray(cache?.conversations)) return;
    cache.conversations = cache.conversations.filter(
      (conversation) =>
        !ids.some((id) => areSameConversationId(id, conversation?.id))
    );
  });
};

const updateConversationInCache = (
  _state,
  conversationId,
  updates = {},
  options = {}
) => {
  if (conversationId == null || !updates || typeof updates !== 'object') {
    return;
  }

  Object.values(_state.conversationCache || {}).forEach((cache) => {
    const conversation = findConversation(cache?.conversations, conversationId);
    if (!conversation) return;

    Object.assign(
      conversation,
      resolveConversationReadStateForStore(
        _state,
        conversation,
        {
          id: conversation.id,
          ...updates,
        },
        options
      )
    );
  });
};

const isNonActivityMessage = (message = {}) =>
  Number(message?.message_type) !== MESSAGE_TYPE.ACTIVITY;

const isOutgoingEmailMessage = (message = {}) =>
  Number(message?.message_type) === MESSAGE_TYPE.OUTGOING &&
  Boolean(message?.content_attributes?.email);

const isIncomingMessage = (message = {}) =>
  Number(message?.message_type) === MESSAGE_TYPE.INCOMING;

const isPublicOutgoingMessage = (message = {}) =>
  Number(message?.message_type) === MESSAGE_TYPE.OUTGOING && !message?.private;

const shouldExposeEmailTrackingPlaceholder = (message = {}) =>
  isOutgoingEmailMessage(message) && message.status !== MESSAGE_STATUS.FAILED;

const getMessageActivityTimestamp = (message = {}) =>
  message?.conversation?.last_activity_at || message?.created_at || null;

const getComparableTimestamp = (value = null) => {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const preserveNewestTimestamp = (currentValue = null, incomingValue = null) => {
  if (
    incomingValue === null ||
    incomingValue === undefined ||
    incomingValue === ''
  ) {
    return currentValue;
  }

  return getComparableTimestamp(incomingValue) >=
    getComparableTimestamp(currentValue)
    ? incomingValue
    : currentValue;
};

const isSameConversationMessage = (currentMessage = {}, incomingMessage = {}) =>
  findPendingMessageIndex({ messages: [currentMessage] }, incomingMessage) ===
  0;

const shouldUpdateConversationPreviewFromMessage = (
  chat,
  message,
  { isNewMessage = false } = {}
) => {
  if (!isNonActivityMessage(message)) return false;

  const currentPreviewMessage = chat?.last_non_activity_message;
  if (!currentPreviewMessage) return true;
  if (isSameConversationMessage(currentPreviewMessage, message)) return true;

  const currentCreatedAt = getComparableTimestamp(
    currentPreviewMessage.created_at
  );
  const incomingCreatedAt = getComparableTimestamp(message.created_at);

  if (incomingCreatedAt !== currentCreatedAt) {
    return incomingCreatedAt > currentCreatedAt;
  }

  // New messages are appended in server order. An update for another message
  // created in the same second must not replace the current preview.
  return isNewMessage;
};

const getLatestConversationNonActivityMessage = (conversation = {}) => {
  const messages = Array.isArray(conversation.messages)
    ? conversation.messages.filter(isNonActivityMessage)
    : [];
  const localMessage = messages.length ? messages[messages.length - 1] : null;
  const apiMessage = conversation.last_non_activity_message || null;

  if (localMessage && apiMessage) {
    return getComparableTimestamp(localMessage.created_at) >=
      getComparableTimestamp(apiMessage.created_at)
      ? localMessage
      : apiMessage;
  }

  return localMessage || apiMessage;
};

const mergeAdditionalAttributesPreservingLocalPreview = (
  existingAttributes = {},
  incomingAttributes = {}
) => {
  const mergedAttributes = {
    ...existingAttributes,
    ...incomingAttributes,
  };

  if (existingAttributes.email_tracking && !incomingAttributes.email_tracking) {
    mergedAttributes.email_tracking = existingAttributes.email_tracking;
  }

  return mergedAttributes;
};

const clearEmailTrackingAttribute = (attributes = {}) => {
  if (!attributes) {
    return {};
  }

  if (!Object.prototype.hasOwnProperty.call(attributes, 'email_tracking')) {
    return attributes;
  }

  const { email_tracking: _emailTracking, ...rest } = attributes;
  return rest;
};

const shouldClearEmailTrackingForIncomingMessage = ({
  existingMessage = null,
  incomingMessage = null,
}) => {
  if (!isIncomingMessage(incomingMessage)) {
    return false;
  }

  if (!existingMessage) {
    return true;
  }

  return (
    getComparableTimestamp(getMessageActivityTimestamp(incomingMessage)) >=
    getComparableTimestamp(getMessageActivityTimestamp(existingMessage))
  );
};

const mergeConversationAttributesPreservingLocalPreview = ({
  existingConversation = {},
  incomingConversation = {},
  incomingAttributes = {},
}) => {
  const mergedAttributes = { ...incomingAttributes };

  mergedAttributes.additional_attributes =
    mergeAdditionalAttributesPreservingLocalPreview(
      existingConversation.additional_attributes || {},
      incomingAttributes.additional_attributes || {}
    );

  // Guardia revision al punto di passaggio unico dei merge: la copia in
  // actionCable copre solo conversation.updated, ma lo snapshot intero viaggia
  // anche su status_changed/assignee/read e nelle risposte REST partite prima
  // di una transizione. Uno snapshot piu' vecchio non deve far ricomparire uno
  // stato Bozza/Invio gia' superato; stato, revision e pending_preview vanno
  // preservati insieme perche' il projector li scrive come unita'.
  const isStaleSnapshot = isStaleConversationAiScheduledStateSnapshot({
    currentConversation: existingConversation,
    incomingConversation,
  });
  if (isStaleSnapshot) {
    const existingAdditionalAttributes =
      existingConversation.additional_attributes || {};
    mergedAttributes.additional_attributes = {
      ...mergedAttributes.additional_attributes,
      [AI_SCHEDULED_STATE_ATTRIBUTE_KEY]:
        existingAdditionalAttributes[AI_SCHEDULED_STATE_ATTRIBUTE_KEY],
      [AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY]:
        existingAdditionalAttributes[AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY],
    };
    mergedAttributes.pending_preview = existingConversation.pending_preview;
  }

  // autoDraft e' un campo solo-FE: gli snapshot dal backend non lo portano mai
  // e lo spread in UPDATE_CONVERSATION lo conserva. Quando la bozza viene
  // annullata (es. risposta manuale da un altro dispositivo) il backend spegne
  // has_pending_ai_draft ma non esiste un evento "canceled" via cable: senza
  // questo azzeramento il tag Bozza resta acceso per sempre in ChatList.
  if (
    !isStaleSnapshot &&
    existingConversation.autoDraft &&
    (incomingAttributes.additional_attributes || {}).has_pending_ai_draft ===
      false
  ) {
    mergedAttributes.autoDraft = null;
  }

  const existingMessage =
    getLatestConversationNonActivityMessage(existingConversation);
  const incomingMessage =
    getLatestConversationNonActivityMessage(incomingConversation);
  const existingMessageTimestamp = getComparableTimestamp(
    getMessageActivityTimestamp(existingMessage)
  );
  const incomingMessageTimestamp = getComparableTimestamp(
    getMessageActivityTimestamp(incomingMessage)
  );

  if (
    existingMessage &&
    (!incomingMessage || existingMessageTimestamp > incomingMessageTimestamp)
  ) {
    mergedAttributes.last_non_activity_message = existingMessage;
    mergedAttributes.timestamp = Math.max(
      getComparableTimestamp(incomingAttributes.timestamp),
      getComparableTimestamp(existingConversation.timestamp),
      existingMessageTimestamp
    );
    mergedAttributes.last_activity_at = Math.max(
      getComparableTimestamp(incomingAttributes.last_activity_at),
      getComparableTimestamp(existingConversation.last_activity_at),
      existingMessageTimestamp
    );
    if (
      typeof existingConversation.messages_count === 'number' &&
      typeof incomingAttributes.messages_count === 'number'
    ) {
      mergedAttributes.messages_count = Math.max(
        existingConversation.messages_count,
        incomingAttributes.messages_count
      );
    }
  }

  if (
    shouldClearEmailTrackingForIncomingMessage({
      existingMessage,
      incomingMessage,
    })
  ) {
    mergedAttributes.additional_attributes = clearEmailTrackingAttribute(
      mergedAttributes.additional_attributes
    );
  }

  return mergedAttributes;
};

const updateConversationPreviewFromMessage = (
  chat,
  message,
  { incrementMessageCount = false, isNewMessage = false } = {}
) => {
  if (!chat || !message) return;

  const activityTimestamp = getMessageActivityTimestamp(message);
  if (activityTimestamp) {
    chat.timestamp = preserveNewestTimestamp(chat.timestamp, activityTimestamp);
    chat.last_activity_at = preserveNewestTimestamp(
      chat.last_activity_at,
      activityTimestamp
    );
  }

  const shouldUpdatePreview = shouldUpdateConversationPreviewFromMessage(
    chat,
    message,
    { isNewMessage }
  );

  if (shouldUpdatePreview) {
    if (activityTimestamp) {
      chat.last_message_at = preserveNewestTimestamp(
        chat.last_message_at,
        activityTimestamp
      );
    }
    chat.last_non_activity_message = message;
  }

  if (
    incrementMessageCount &&
    isNonActivityMessage(message) &&
    typeof chat.messages_count === 'number'
  ) {
    chat.messages_count += 1;
  }

  if (isIncomingMessage(message)) {
    chat.last_incoming_at = preserveNewestTimestamp(
      chat.last_incoming_at,
      message.created_at
    );
    if (shouldUpdatePreview) {
      chat.has_ai_reply = false;
      chat.additional_attributes = clearEmailTrackingAttribute(
        chat.additional_attributes
      );
    }
  }

  // Solo le righe della cartella Inviata portano last_outgoing_at.
  if (chat.last_outgoing_at !== undefined && isPublicOutgoingMessage(message)) {
    chat.last_outgoing_at = preserveNewestTimestamp(
      chat.last_outgoing_at,
      message.created_at
    );
  }

  if (shouldUpdatePreview && shouldExposeEmailTrackingPlaceholder(message)) {
    chat.additional_attributes = {
      ...(chat.additional_attributes || {}),
      email_tracking: message.additional_attributes?.email_tracking || {
        opened: false,
        open_count: 0,
      },
    };
  }
};

const replaceConversationAt = (_state, index, conversation) => {
  _state.allConversations.splice(index, 1, conversation);
};

const patchConversation = (_state, conversationId, patchBuilder) => {
  const index = findConversationIndex(_state.allConversations, conversationId);
  if (index === -1) {
    return null;
  }

  const current = _state.allConversations[index];
  const patch =
    typeof patchBuilder === 'function' ? patchBuilder(current) : patchBuilder;
  const updated = { ...current, ...patch };
  replaceConversationAt(_state, index, updated);
  return updated;
};

const DRAFT_GENERATION_LEGACY_ID = '__legacy__';
const DRAFT_GENERATION_HISTORY_LIMIT = 5;

const normalizeDraftGenerationId = (generationId) => {
  if (generationId === null || generationId === undefined) return null;
  const normalized = String(generationId).trim();
  return normalized || null;
};

const uniqueDraftGenerationIds = (generationIds = []) => [
  ...new Set(generationIds.filter(Boolean)),
];

const buildDraftGenerationPatch = (
  conversation = {},
  { generationId = null, isGenerating = false } = {}
) => {
  const normalizedGenerationId = normalizeDraftGenerationId(generationId);
  const completedGenerationIds = uniqueDraftGenerationIds(
    conversation.completedDraftGenerationIds || []
  );
  let activeGenerationIds = uniqueDraftGenerationIds(
    conversation.activeDraftGenerationIds || []
  );

  if (isGenerating) {
    if (
      normalizedGenerationId &&
      completedGenerationIds.includes(normalizedGenerationId)
    ) {
      return {};
    }

    if (normalizedGenerationId) {
      activeGenerationIds = activeGenerationIds.filter(
        (id) => id !== DRAFT_GENERATION_LEGACY_ID
      );
      activeGenerationIds.push(normalizedGenerationId);
    } else if (!activeGenerationIds.length) {
      activeGenerationIds.push(DRAFT_GENERATION_LEGACY_ID);
    }
  } else if (normalizedGenerationId) {
    activeGenerationIds = activeGenerationIds.filter(
      (id) => id !== normalizedGenerationId && id !== DRAFT_GENERATION_LEGACY_ID
    );
    completedGenerationIds.push(normalizedGenerationId);
  } else {
    activeGenerationIds = [];
  }

  const normalizedActiveIds = uniqueDraftGenerationIds(activeGenerationIds);
  const normalizedCompletedIds = uniqueDraftGenerationIds(
    completedGenerationIds
  ).slice(-DRAFT_GENERATION_HISTORY_LIMIT);

  return {
    activeDraftGenerationIds: normalizedActiveIds,
    completedDraftGenerationIds: normalizedCompletedIds,
    isGeneratingDraft: normalizedActiveIds.length > 0,
  };
};

const patchConversationAndCache = (_state, conversationId, patchBuilder) => {
  patchConversation(_state, conversationId, patchBuilder);

  Object.values(_state.conversationCache || {}).forEach((cache) => {
    const conversation = findConversation(cache?.conversations, conversationId);
    if (!conversation) return;

    const patch =
      typeof patchBuilder === 'function'
        ? patchBuilder(conversation)
        : patchBuilder;
    Object.assign(conversation, patch);
  });
};

// Gli indicatori (programmato, bozza) vanno scritti anche nelle pagine in
// cache: patchConversation da solo tocca allConversations, e cambiando
// cartella e tornando indietro l'icona risorge nello stato vecchio.
const patchConversationAdditionalAttributes = (
  _state,
  conversationId,
  attributes
) => {
  const mergeAttributes = (conversation) => ({
    additional_attributes: {
      ...(conversation?.additional_attributes || {}),
      ...attributes,
    },
  });

  patchConversation(_state, conversationId, mergeAttributes);

  Object.values(_state.conversationCache || {}).forEach((cache) => {
    const conversation = findConversation(cache?.conversations, conversationId);
    if (!conversation) return;

    Object.assign(conversation, mergeAttributes(conversation));
  });
};

// mutations

export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    // Ricostruisci lo stato secondo l'ordine del payload ricevuto
    const existingById = _state.allConversations.reduce((acc, conv) => {
      acc[conv.id] = conv;
      return acc;
    }, {});
    const visibleConversationList = filterPendingPlacementConversations({
      conversations: conversationList,
      pendingOperations: _state.pendingPlacementOperations,
      source: CONVERSATION_PLACEMENT_SOURCE.FETCH,
    });
    const selectedConversation = getSelectedConversationSnapshot(_state);

    const mergedInIncomingOrder = visibleConversationList.map((conv) => {
      const prev = existingById[conv.id];
      pinFrozenConversationPlacementForStore(_state, prev || conv);
      if (!prev) return conv;
      return mergeConversationForListUpdate(_state, prev, conv);
    });

    const shouldPreserveSelectedConversation =
      selectedConversation &&
      !getPendingPlacementOperation(_state, selectedConversation.id) &&
      !mergedInIncomingOrder.some((conversation) =>
        areSameConversationId(conversation.id, _state.selectedChatId)
      );

    _state.allConversations = shouldPreserveSelectedConversation
      ? mergedInIncomingOrder.concat([selectedConversation])
      : mergedInIncomingOrder;
    setSelectedConversationSnapshot(
      _state,
      findConversation(_state.allConversations, _state.selectedChatId) ||
        selectedConversation
    );
  },
  // Delta del reconnect: la risposta contiene solo le conversazioni toccate
  // durante lo stacco, quindi non può sostituire la lista. Le righe già
  // presenti ricevono lo stesso trattamento di SET_ALL_CONVERSATION (stato di
  // lettura + anteprima), sopra i campi che avevano di locale; le righe nuove
  // passano dal percorso di inserimento di UPDATE_CONVERSATION. Le righe che
  // il delta non porta restano dove sono.
  [types.MERGE_CONVERSATION_LIST_DELTA](_state, conversationList = []) {
    const visibleConversationList = filterPendingPlacementConversations({
      conversations: conversationList,
      pendingOperations: _state.pendingPlacementOperations,
      source: CONVERSATION_PLACEMENT_SOURCE.FETCH,
    });

    visibleConversationList.forEach((conversation) => {
      if (!conversation?.id) return;

      const index = findConversationIndex(
        _state.allConversations,
        conversation.id
      );

      if (index === -1) {
        mutations[types.UPDATE_CONVERSATION](_state, {
          ...conversation,
          __placement_source: CONVERSATION_PLACEMENT_SOURCE.FETCH,
        });
        pinFrozenConversationPlacementForStore(
          _state,
          findConversation(_state.allConversations, conversation.id)
        );
        return;
      }

      const prev = _state.allConversations[index];
      pinFrozenConversationPlacementForStore(_state, prev);
      replaceConversationAt(_state, index, {
        ...prev,
        ...mergeConversationForListUpdate(_state, prev, conversation),
        id: prev.id,
      });
      setSelectedConversationSnapshot(_state, _state.allConversations[index]);
    });
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    // Salva l'ID della chat selezionata prima di svuotare la lista
    const previousSelectedChatId = _state.selectedChatId;
    // Salva anche i dettagli della conversazione selezionata se esiste
    let selectedChatDetails = null;
    if (previousSelectedChatId) {
      selectedChatDetails = getSelectedConversationSnapshot(_state);
    }

    _state.allConversations = [];
    _state.freshArrivals = {};
    // Non azzeriamo più l'ID della chat selezionata
    // _state.selectedChatId = null;
    _state.totalConversations = 0;

    // Ripristiniamo l'ID della chat selezionata
    _state.selectedChatId = previousSelectedChatId;

    // Se abbiamo salvato i dettagli della chat, li rimettiamo nell'array
    // In questo modo, anche se la chat non dovesse tornare dall'API,
    // almeno manteniamo i dati che avevamo
    if (selectedChatDetails) {
      _state.allConversations.push(selectedChatDetails);
    }
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId = null) {
    const chat = conversationId
      ? _state.allConversations.find(
          (c) => String(c.id) === String(conversationId)
        )
      : getSelectedChatConversation(_state)[0];
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId = null) {
    const chat = conversationId
      ? _state.allConversations.find(
          (c) => String(c.id) === String(conversationId)
        )
      : getSelectedChatConversation(_state)[0];
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
    _state.selectedChat = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](
    _state,
    { id, data, direction = 'before' }
  ) {
    if (data.length) {
      const chat = findConversation(_state.allConversations, id);
      if (chat) {
        // Assicuriamoci che l'array messages esista
        if (!chat.messages) {
          chat.messages = [];
        }
        chat.messages = mergeConversationMessagesForDetailUpdate(
          chat.messages,
          data,
          { direction }
        );
      }
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const chat = findConversation(_state.allConversations, id);
    if (!chat) return;
    chat.attachments = [];
    chat.attachments.push(...data);
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const chat = findConversation(_state.allConversations, id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
      _state.selectedChat = activeChat;
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chat] = getSelectedChatConversation(_state);
    chat.meta.assignee = assignee;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const chat = findConversation(_state.allConversations, conversationId);
    if (chat) {
      pinFrozenConversationPlacementForStore(_state, chat);
      chat.last_activity_at = lastActivityAt;
    }
  },

  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const chat = findConversation(_state.allConversations, conversationId);
    if (chat) {
      chat.priority = priority;
    }
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chat] = getSelectedChatConversation(_state);
    chat.custom_attributes = custom_attributes;
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
    updateConversationInCache(_state, conversationId, {
      snoozed_until: snoozedUntil,
      status,
    });
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS]({ allConversations }, message) {
    const conversationId = message?.conversation_id;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    if (!chat || message?.status !== MESSAGE_STATUS.SENT) return;

    const conversationAttachments = ensureConversationAttachments(chat);
    if (!Array.isArray(message.attachments)) return;

    const existingAttachmentIds = new Set(
      conversationAttachments
        .map((attachment) => normalizeAttachmentId(attachment?.id))
        .filter((attachmentId) => attachmentId !== null)
    );

    message.attachments.forEach((attachment) => {
      if (!attachment || typeof attachment !== 'object') return;

      const attachmentId = normalizeAttachmentId(attachment.id);
      if (attachmentId === null || existingAttachmentIds.has(attachmentId)) {
        return;
      }

      conversationAttachments.push(attachment);
      existingAttachmentIds.add(attachmentId);
    });
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS]({ allConversations }, message) {
    const conversationId = message?.conversation_id;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    if (!chat || message?.status !== MESSAGE_STATUS.SENT) return;

    const conversationAttachments = ensureConversationAttachments(chat);
    const messageId = normalizeAttachmentId(message.id);
    if (messageId === null) return;

    for (
      let index = conversationAttachments.length - 1;
      index >= 0;
      index -= 1
    ) {
      const attachmentMessageId = normalizeAttachmentId(
        conversationAttachments[index]?.message_id
      );
      if (attachmentMessageId === messageId) {
        conversationAttachments.splice(index, 1);
      }
    }
  },

  // Aggiorna le reactions di un messaggio specifico
  [types.UPDATE_MESSAGE_REACTIONS](
    { allConversations },
    { conversationId, messageId, reactions }
  ) {
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const messageIndex = chat.messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) return;

    const message = chat.messages[messageIndex];
    // Crea un nuovo oggetto content_attributes per trigggerare la reattività
    const newContentAttributes = {
      ...(message.content_attributes || {}),
      reactions,
    };
    message.content_attributes = newContentAttributes;
  },

  [types.ADD_MESSAGE](_state, message) {
    const { allConversations, selectedChatId } = _state;
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    pinFrozenConversationPlacementForStore(_state, chat);
    if (
      chat.archive_version != null &&
      isIncomingMessage(message) &&
      Number.isSafeInteger(message.id)
    ) {
      chat.archive_version = Math.max(chat.archive_version, message.id);
    }

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      // Preserve email html_content/text_content from the pending message
      // when the incoming broadcast has them stripped (payload size optimization).
      const pending = chat.messages[pendingMessageIndex];
      if (isOlderMessageSnapshot(pending, message)) {
        return;
      }
      const pendingEmail = pending?.content_attributes?.email;
      const incomingEmail = message?.content_attributes?.email;

      if (
        pendingEmail &&
        incomingEmail &&
        (pendingEmail.html_content || pendingEmail.text_content) &&
        !incomingEmail.html_content &&
        !incomingEmail.text_content
      ) {
        const mergedEmail = { ...incomingEmail };
        if (pendingEmail.html_content)
          mergedEmail.html_content = pendingEmail.html_content;
        if (pendingEmail.text_content)
          mergedEmail.text_content = pendingEmail.text_content;

        message = {
          ...message,
          content_attributes: {
            ...message.content_attributes,
            email: mergedEmail,
          },
        };
      }

      const hasIncomingAttachments =
        Array.isArray(message.attachments) && message.attachments.length > 0;
      const nextMessage =
        !hasIncomingAttachments &&
        Array.isArray(pending.attachments) &&
        pending.attachments.length > 0
          ? {
              ...message,
              attachments: pending.attachments,
            }
          : message;

      chat.messages[pendingMessageIndex] = nextMessage;
      updateConversationPreviewFromMessage(chat, nextMessage);
    } else {
      chat.messages.push(message);
      updateConversationPreviewFromMessage(chat, message, {
        incrementMessageCount: true,
        isNewMessage: true,
      });
      // Aggiorna unread_count solo se il backend lo fornisce esplicitamente
      const unreadCountRaw = message?.conversation?.unread_count;
      if (typeof unreadCountRaw === 'number') {
        chat.unread_count = unreadCountRaw;
      }
      // Ordine pagine >1 congelato: il getter non riordina, quindi il nuovo
      // messaggio deve portare la conversazione in testa esplicitamente
      // (comportamento WhatsApp). A pagina 1 ci pensa il sort del getter.
      if (
        shouldFloatConversationOnRealtimeMessage({
          chatSortFilter: _state.chatSortFilter,
          currentPage: _state.conversationFilters?.page,
          message,
        })
      ) {
        floatConversationToTopUnlessFrozen(_state, conversationId);
      }
      if (areSameConversationId(selectedChatId, conversationId)) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        if (!isOutgoingEmailMessage(message)) {
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const placementResolution = resolveConversationPlacementForStore(
      _state,
      null,
      conversation,
      { source: CONVERSATION_PLACEMENT_SOURCE.REALTIME }
    );
    if (!placementResolution.shouldApply) {
      return;
    }

    const resolvedConversation = placementResolution.conversation;
    // Applica cache di bozze completate se presente
    if (_state.draftCompletionCache?.[resolvedConversation.id]) {
      const cached = _state.draftCompletionCache[resolvedConversation.id];
      Object.assign(resolvedConversation, {
        autoDraft: cached.draft,
        ...buildDraftGenerationPatch(resolvedConversation, {
          generationId: cached.generationId,
          isGenerating: false,
        }),
      });
      delete _state.draftCompletionCache[resolvedConversation.id];
    }
    insertRealtimeConversation(_state, resolvedConversation);
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    if (!conversation?.id) {
      return;
    }

    const { allConversations } = _state;
    const index = findConversationIndex(allConversations, conversation.id);

    // Applica cache di bozze completate se presente
    if (_state.draftCompletionCache?.[conversation.id]) {
      const cached = _state.draftCompletionCache[conversation.id];
      Object.assign(conversation, {
        autoDraft: cached.draft,
        ...buildDraftGenerationPatch(conversation, {
          generationId: cached.generationId,
          isGenerating: false,
        }),
      });
      delete _state.draftCompletionCache[conversation.id];
    }

    const {
      __placement_source: placementSource = CONVERSATION_PLACEMENT_SOURCE.FETCH,
      __read_state_message_created_at: readStateMessageCreatedAt,
      __read_state_source: readStateSource,
      messages,
      ...incomingConversationAttributes
    } = conversation;

    if (index > -1) {
      pinFrozenConversationPlacementForStore(_state, allConversations[index]);
      const conversationAttributes =
        mergeConversationAttributesPreservingLocalPreview({
          existingConversation: allConversations[index],
          incomingConversation: conversation,
          incomingAttributes: incomingConversationAttributes,
        });
      const placementResolution = resolveConversationPlacementForStore(
        _state,
        allConversations[index],
        {
          id: conversation.id,
          ...conversationAttributes,
        },
        { source: placementSource }
      );
      if (!placementResolution.shouldApply) {
        return;
      }
      const resolvedConversationAttributes =
        resolveConversationReadStateForStore(
          _state,
          allConversations[index],
          placementResolution.conversation,
          {
            messageCreatedAt: readStateMessageCreatedAt,
            source: readStateSource,
          }
        );
      const updatedConversation = {
        ...allConversations[index],
        ...resolvedConversationAttributes,
        id: allConversations[index].id,
      };
      allConversations.splice(index, 1, updatedConversation);
      setSelectedConversationSnapshot(_state, updatedConversation);
      updateConversationInCache(
        _state,
        conversation.id,
        resolvedConversationAttributes,
        {
          messageCreatedAt: readStateMessageCreatedAt,
          source: readStateSource,
        }
      );
    } else {
      const incomingConversationForInsert =
        messages === undefined
          ? incomingConversationAttributes
          : { ...incomingConversationAttributes, messages };
      const placementResolution = resolveConversationPlacementForStore(
        _state,
        null,
        incomingConversationForInsert,
        { source: placementSource }
      );
      if (placementResolution.shouldApply) {
        if (placementSource === CONVERSATION_PLACEMENT_SOURCE.REALTIME) {
          insertRealtimeConversation(_state, placementResolution.conversation);
        } else {
          _state.allConversations.push(placementResolution.conversation);
        }
        setSelectedConversationSnapshot(
          _state,
          placementResolution.conversation
        );
      } else if (
        areSameConversationId(conversation.id, _state.selectedChatId)
      ) {
        const selectedConversation = _state.selectedChat || {
          id: _state.selectedChatId,
        };
        const selectedConversationAttributes =
          mergeConversationAttributesPreservingLocalPreview({
            existingConversation: selectedConversation,
            incomingConversation: conversation,
            incomingAttributes: incomingConversationForInsert,
          });
        _state.selectedChat = {
          ...selectedConversation,
          ...selectedConversationAttributes,
          id: selectedConversation.id,
        };
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.SET_UNREAD_FIRST](_state, enabled) {
    _state.unreadFirstEnabled = enabled;
  },

  [types.SET_PRIORITY_FILTER](_state, enabled) {
    _state.priorityFilterEnabled = enabled;
    _state.conversationCache = {};
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = findConversation(_state.allConversations, payload.id);
    if (chat?.meta) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const chat = findConversation(_state.allConversations, conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_ACTIVE_INBOX_FILTER](
    _state,
    { inboxId, inboxName, filterType, filterValue, filterLabel }
  ) {
    _state.activeInboxFilter = {
      inboxId,
      inboxName,
      filterType,
      filterValue,
      filterLabel,
    };
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    if (!conversationId) {
      return;
    }
    const conversation = findConversation(
      _state.allConversations,
      conversationId
    );
    if (conversation) {
      conversation.can_reply = canReply;
    }
  },

  [types.REMOVE_RESOLVED_CONVERSATIONS](_state, resolvedIds) {
    _state.allConversations = _state.allConversations.filter((conversation) => {
      return !resolvedIds.some((id) =>
        areSameConversationId(id, conversation.id)
      );
    });
    removeConversationsFromCache(_state, resolvedIds);
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      (c) => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  // Contatto unito a un altro: le sue conversazioni restano in lista, con
  // il contatto rimasto come mittente.
  [types.MOVE_CONTACT_CONVERSATIONS](_state, { fromContactId, sender }) {
    _state.allConversations.forEach((chat) => {
      if (chat.meta?.sender?.id === fromContactId) {
        chat.meta.sender = sender;
      }
    });
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
    // Ripulisci i conversationFilters da chiavi che possono vincolare la vista
    if (_state.conversationFilters) {
      const keysToDelete = [
        'assignee_id',
        'inboxId',
        'labels',
        'label_id',
        'label_source_id',
        'conversationType',
        'contact_id',
        'include_labeled',
      ];

      keysToDelete.forEach((key) => {
        if (key in _state.conversationFilters) {
          delete _state.conversationFilters[key];
        }
      });
    }
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { accountId, conversationId, messageId }
  ) {
    const syncCursorKey = `${accountId ?? 'unknown'}:${conversationId}`;
    _state.syncConversationsMessages[syncCursorKey] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.REMOVE_PROPERTY_FROM_FILTERS](_state, propertyName) {
    if (propertyName in _state.conversationFilters) {
      delete _state.conversationFilters[propertyName];
    }
  },
  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    {
      id,
      lastSeen,
      messageCreatedAt = null,
      source = 'generic',
      unreadCount = 0,
    }
  ) {
    try {
      const index = findConversationIndex(_state.allConversations, id);
      if (index === -1) {
        return;
      }

      const currentConversation = _state.allConversations[index];
      pinFrozenConversationPlacementForStore(_state, currentConversation);
      const incomingPatch = {
        id: currentConversation.id,
        ...buildConversationUnreadPatch({ lastSeen, unreadCount }),
      };
      const resolvedPatch = resolveConversationReadStateForStore(
        _state,
        currentConversation,
        incomingPatch,
        { messageCreatedAt, source }
      );
      const updatedConversation = {
        ...currentConversation,
        ...resolvedPatch,
      };

      _state.allConversations = _state.allConversations.slice();
      _state.allConversations.splice(index, 1, updatedConversation);
      updateConversationInCache(
        _state,
        id,
        {
          agent_last_seen_at: updatedConversation.agent_last_seen_at,
          unread_count: updatedConversation.unread_count,
        },
        { messageCreatedAt, source }
      );
    } catch (error) {
      // Keep unread state updates best effort.
    }
  },

  [types.SET_CONVERSATION_NEW_MESSAGE_BADGE](
    _state,
    {
      conversationId = null,
      id = null,
      messageId = null,
      expiresAt = null,
    } = {}
  ) {
    const targetId = conversationId ?? id;
    const normalizedExpiresAt = Number(expiresAt);
    if (targetId === null || targetId === undefined) {
      return;
    }
    if (!Number.isFinite(normalizedExpiresAt) || normalizedExpiresAt <= 0) {
      return;
    }

    const patch = {
      [CONVERSATION_NEW_MESSAGE_BADGE_ID_FIELD]: messageId ?? null,
      [CONVERSATION_NEW_MESSAGE_BADGE_EXPIRES_AT_FIELD]: normalizedExpiresAt,
    };
    patchConversation(_state, targetId, patch);
    updateConversationInCache(_state, targetId, patch);
  },

  [types.CLEAR_CONVERSATION_NEW_MESSAGE_BADGE](
    _state,
    { conversationId = null, id = null, messageId = null } = {}
  ) {
    const targetId = conversationId ?? id;
    if (targetId === null || targetId === undefined) {
      return;
    }

    const conversation = findConversation(_state.allConversations, targetId);
    if (!conversation) {
      return;
    }

    const currentMessageId =
      conversation[CONVERSATION_NEW_MESSAGE_BADGE_ID_FIELD];
    if (
      messageId !== null &&
      messageId !== undefined &&
      currentMessageId !== null &&
      currentMessageId !== undefined &&
      String(currentMessageId) !== String(messageId)
    ) {
      return;
    }

    const patch = {
      [CONVERSATION_NEW_MESSAGE_BADGE_ID_FIELD]: null,
      [CONVERSATION_NEW_MESSAGE_BADGE_EXPIRES_AT_FIELD]: null,
    };
    patchConversation(_state, targetId, patch);
    updateConversationInCache(_state, targetId, patch);
  },

  [types.SET_CONVERSATION_READ_STATE_OPERATION](
    _state,
    { agentLastSeenAt = null, id = null, nowMs = () => Date.now() } = {}
  ) {
    const operation = buildConversationReadStateOperation({
      agentLastSeenAt,
      id,
      nowMs,
    });
    if (!operation) return;

    _state.pendingReadStateOperations = {
      ...(_state.pendingReadStateOperations || {}),
      [operation.id]: operation,
    };
  },

  [types.CLEAR_CONVERSATION_READ_STATE_OPERATION](
    _state,
    { id = null, startedAt = null } = {}
  ) {
    if (id === null || id === undefined || id === '') return;

    const key = String(id);
    const operation = _state.pendingReadStateOperations?.[key];
    if (!operation) return;

    if (startedAt !== null && operation.startedAt !== startedAt) {
      return;
    }

    const { [key]: _removed, ...remainingOperations } =
      _state.pendingReadStateOperations || {};
    _state.pendingReadStateOperations = remainingOperations;
  },

  [types.SET_CONVERSATION_PLACEMENT_OPERATION](
    _state,
    {
      id = null,
      inboxId = null,
      kind = 'folder',
      nowMs = () => Date.now(),
      removeFromList = true,
      status = null,
      targetFolderId = null,
      targetFolderTitle = null,
      targetLabelId = null,
      targetLabelTitle = null,
    } = {}
  ) {
    const previousOperation =
      _state.pendingPlacementOperations?.[String(id)] || null;
    const shouldPreserveDestination =
      previousOperation &&
      previousOperation.kind === kind &&
      previousOperation.status === status;
    const preservedTargetFolderId = shouldPreserveDestination
      ? previousOperation.targetFolderId
      : null;
    const preservedTargetFolderTitle = shouldPreserveDestination
      ? previousOperation.targetFolderTitle
      : null;
    const preservedTargetLabelId = shouldPreserveDestination
      ? previousOperation.targetLabelId
      : null;
    const preservedTargetLabelTitle = shouldPreserveDestination
      ? previousOperation.targetLabelTitle
      : null;
    const operation = buildConversationPlacementOperation({
      id,
      inboxId,
      kind,
      nowMs,
      removeFromList,
      status,
      targetFolderId: targetFolderId ?? preservedTargetFolderId,
      targetFolderTitle: targetFolderTitle ?? preservedTargetFolderTitle,
      targetLabelId: targetLabelId ?? preservedTargetLabelId,
      targetLabelTitle: targetLabelTitle ?? preservedTargetLabelTitle,
    });
    if (!operation) return;

    _state.pendingPlacementOperations = {
      ...(_state.pendingPlacementOperations || {}),
      [operation.id]: operation,
    };

    if (operation.removeFromList) {
      removeConversationsFromCache(_state, [operation.id]);
    }
  },

  [types.CLEAR_CONVERSATION_PLACEMENT_OPERATION](
    _state,
    { id = null, startedAt = null } = {}
  ) {
    if (id === null || id === undefined || id === '') return;

    const key = String(id);
    const operation = _state.pendingPlacementOperations?.[key];
    if (!operation) return;

    if (startedAt !== null && operation.startedAt !== startedAt) {
      return;
    }

    const { [key]: _removed, ...remainingOperations } =
      _state.pendingPlacementOperations || {};
    _state.pendingPlacementOperations = remainingOperations;
  },

  // typing_on/typing_off di una conversazione (actionCable): inbox e record
  // di typing arrivano dall'action, che legge gli altri moduli. Con Otello al
  // lavoro su una inbox API la riga viene congelata (idempotente: il typing
  // viene rinnovato ogni pochi secondi); altrimenti viene sbloccata e, se
  // sulle pagine >1 una risalita era stata rimandata, portata in testa ora.
  [types.SYNC_CONVERSATION_PLACEMENT_FREEZE](
    _state,
    { conversationId = null, inbox = null, typingUsersList = [] } = {}
  ) {
    if (conversationId === null || conversationId === undefined) return;

    if (shouldFreezeConversationPlacement({ inbox, typingUsersList })) {
      const nextFrozenPlacements = freezeConversationPlacement({
        conversation: findConversation(_state.allConversations, conversationId),
        conversationId,
        frozenPlacements: _state.frozenPlacements,
      });
      if (nextFrozenPlacements) {
        _state.frozenPlacements = nextFrozenPlacements;
      }
      return;
    }

    const release = releaseConversationPlacement({
      conversationId,
      frozenPlacements: _state.frozenPlacements,
    });
    if (!release) return;

    _state.frozenPlacements = release.frozenPlacements;
    if (release.floatOnRelease) {
      floatConversationToTop(_state, conversationId);
    }
  },

  // La card lo committa allo scadere della finestra "Nuovo": la voce non
  // serve piu' e la mappa resta piccola anche senza nuovi arrivi.
  [types.CLEAR_CONVERSATION_FRESH_ARRIVAL](
    _state,
    { conversationId = null } = {}
  ) {
    const nextFreshArrivals = clearConversationFreshArrival({
      conversationId,
      freshArrivals: _state.freshArrivals,
    });
    if (nextFreshArrivals) {
      _state.freshArrivals = nextFreshArrivals;
    }
  },

  [types.SET_PAGINATION_LOADING_STATUS](_state, status) {
    _state.paginationLoading = status;
  },

  [types.SET_HAS_NEW_CONVERSATIONS](_state, hasNew) {
    _state.hasNewConversations = hasNew;
  },

  [types.SET_LAST_FETCHED_CONVERSATION_ID](_state, id) {
    _state.lastFetchedConversationId = id;
  },

  // Nuova mutation per aggiornare completamente una conversazione
  updateConversation(_state, conversation) {
    const index = findConversationIndex(
      _state.allConversations,
      conversation.id
    );

    if (index !== -1) {
      const {
        __placement_source:
          placementSource = CONVERSATION_PLACEMENT_SOURCE.FETCH,
        ...incomingConversationAttributes
      } = conversation;
      const placementResolution = resolveConversationPlacementForStore(
        _state,
        _state.allConversations[index],
        incomingConversationAttributes,
        { source: placementSource }
      );
      if (!placementResolution.shouldApply) {
        return;
      }
      const resolvedConversation = preserveDraftGenerationState(
        _state.allConversations[index],
        resolveConversationReadStateForStore(
          _state,
          _state.allConversations[index],
          placementResolution.conversation
        )
      );
      _state.allConversations[index] = resolvedConversation;
      setSelectedConversationSnapshot(_state, resolvedConversation);
      updateConversationInCache(_state, conversation.id, resolvedConversation);
    } else if (areSameConversationId(conversation.id, _state.selectedChatId)) {
      const selectedConversation = _state.selectedChat || {
        id: _state.selectedChatId,
      };
      const selectedConversationAttributes =
        mergeConversationAttributesPreservingLocalPreview({
          existingConversation: selectedConversation,
          incomingConversation: conversation,
          incomingAttributes: conversation,
        });
      _state.selectedChat = {
        ...selectedConversation,
        ...selectedConversationAttributes,
        id: selectedConversation.id,
      };
    }
  },

  // Mutation per aggiornare lo stato starred di una conversazione
  updateConversationStarred(_state, { id, starred }) {
    const index = findConversationIndex(_state.allConversations, id);
    const updateStarredFields = (conversation) => {
      ['is_special', 'is_starred', 'starred'].forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(conversation, field)) {
          conversation[field] = starred;
        }
      });

      if (
        !Object.prototype.hasOwnProperty.call(conversation, 'is_special') &&
        !Object.prototype.hasOwnProperty.call(conversation, 'is_starred') &&
        !Object.prototype.hasOwnProperty.call(conversation, 'starred')
      ) {
        conversation.is_special = starred;
      }
    };

    if (index !== -1) {
      updateStarredFields(_state.allConversations[index]);
    }

    if (
      _state.selectedChat &&
      areSameConversationId(_state.selectedChat.id, id)
    ) {
      updateStarredFields(_state.selectedChat);
    }
  },

  // Mutations universali per il cache delle conversazioni
  ['SET_CONVERSATION_CACHE'](
    _state,
    {
      cacheKey,
      conversations,
      filters,
      inboxType,
      totalCount = null,
      meta = null,
    }
  ) {
    const cachedConversations = filterPendingPlacementConversations({
      conversations: cloneConversationCacheList(conversations),
      pendingOperations: _state.pendingPlacementOperations,
      source: CONVERSATION_PLACEMENT_SOURCE.CACHE,
    }).map((conversation) => {
      const currentConversation = findConversation(
        _state.allConversations,
        conversation.id
      );

      if (!currentConversation) return conversation;

      const placementResolution = resolveConversationPlacementForStore(
        _state,
        currentConversation,
        conversation,
        { source: CONVERSATION_PLACEMENT_SOURCE.CACHE }
      );
      if (!placementResolution.shouldApply) {
        return currentConversation;
      }

      return resolveConversationReadStateForStore(
        _state,
        currentConversation,
        placementResolution.conversation
      );
    });

    _state.conversationCache[cacheKey] = {
      conversations: cachedConversations,
      timestamp: Date.now(),
      filters: clonePlainValue(filters || {}),
      inboxType: inboxType || 'unknown',
      totalCount,
      meta: clonePlainValue(meta),
    };
  },

  ['CLEAR_CONVERSATION_CACHE'](_state, cacheKey = null) {
    if (cacheKey) {
      delete _state.conversationCache[cacheKey];
    } else {
      _state.conversationCache = {};
    }
  },

  ['CLEAR_EXPIRED_CONVERSATION_CACHE'](_state) {
    const now = Date.now();

    Object.keys(_state.conversationCache).forEach((key) => {
      const cache = _state.conversationCache[key];
      if (!cache) return;

      const inboxType = cache.inboxType || 'unknown';
      const expiration =
        _state.cacheExpirationByType[inboxType] ||
        _state.cacheExpirationByType.default;

      if (now - cache.timestamp > expiration) {
        delete _state.conversationCache[key];
      }
    });
  },

  ['INVALIDATE_CACHE_FOR_INBOX'](_state, inboxId) {
    const keysToDelete = [];
    const normalizedInboxId = String(inboxId);

    Object.keys(_state.conversationCache).forEach((key) => {
      const cache = _state.conversationCache[key];
      const cacheInboxId = cache?.filters?.inboxId;
      const matchesInboxFilter =
        cacheInboxId !== null &&
        cacheInboxId !== undefined &&
        String(cacheInboxId) === normalizedInboxId;

      if (
        key.startsWith(`${normalizedInboxId}-`) ||
        key.startsWith(`${normalizedInboxId}:`) ||
        key.startsWith(`inbox:${normalizedInboxId}`) ||
        matchesInboxFilter
      ) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      delete _state.conversationCache[key];
    });
  },

  // Mutation per classificazione automatica messaggi
  ['SET_CONVERSATION_CLASSIFICATION'](
    _state,
    { conversationId, classification }
  ) {
    patchConversation(_state, conversationId, {
      lastMessageClassification: classification,
    });
  },

  // Mutation per bozza auto-generata
  // Il canale non e' nel payload: l'indicatore programmato lo accende
  // l'handler websocket, che sa risolvere l'inbox.
  ['SET_CONVERSATION_AUTO_DRAFT'](
    _state,
    { conversationId, draft, generationId = null }
  ) {
    patchConversationAndCache(_state, conversationId, (conversation) => ({
      autoDraft: draft,
      ...buildDraftGenerationPatch(conversation, {
        generationId,
        isGenerating: false,
      }),
    }));
    patchConversationAdditionalAttributes(_state, conversationId, {
      has_pending_ai_draft: true,
      [AI_NEEDS_HUMAN_ATTRIBUTE_KEY]: false,
    });
  },

  ['SET_CONVERSATION_HAS_SCHEDULED_EMAIL'](
    _state,
    { conversationId, hasScheduledEmail }
  ) {
    patchConversationAdditionalAttributes(_state, conversationId, {
      has_scheduled_email: Boolean(hasScheduledEmail),
    });
  },

  ['SET_CONVERSATION_HAS_SCHEDULED_MESSAGE'](
    _state,
    { conversationId, hasScheduledMessage }
  ) {
    patchConversationAdditionalAttributes(_state, conversationId, {
      has_scheduled_message: Boolean(hasScheduledMessage),
    });
  },

  // Reset indicatore bozza quando l'utente apre la conversazione
  ['CLEAR_CONVERSATION_AUTO_DRAFT'](_state, conversationId) {
    patchConversationAndCache(_state, conversationId, {
      activeDraftGenerationIds: [],
      autoDraft: null,
      isGeneratingDraft: false,
    });
    // Reset anche i flag persistenti: bozza in attesa e "Tocca a te"
    patchConversationAdditionalAttributes(_state, conversationId, {
      has_pending_ai_draft: false,
      [AI_NEEDS_HUMAN_ATTRIBUTE_KEY]: false,
    });
  },

  // Stato "in generazione" mentre il job è in corso
  ['SET_CONVERSATION_GENERATING_DRAFT'](
    _state,
    { conversationId, generationId = null, isGenerating }
  ) {
    const normalizedGenerationId = normalizeDraftGenerationId(generationId);
    const cachedCompletion = _state.draftCompletionCache?.[conversationId];
    if (
      isGenerating &&
      normalizedGenerationId &&
      normalizeDraftGenerationId(cachedCompletion?.generationId) ===
        normalizedGenerationId
    ) {
      return;
    }

    patchConversationAndCache(_state, conversationId, (conversation) =>
      buildDraftGenerationPatch(conversation, {
        generationId: normalizedGenerationId,
        isGenerating,
      })
    );
  },

  // Cache delle bozze completate per conversazioni non in memoria
  // Usata quando la conversazione non è in allConversations al momento del WebSocket
  ['CACHE_DRAFT_COMPLETION'](
    _state,
    { conversationId, draft, generationId = null }
  ) {
    if (!_state.draftCompletionCache) {
      _state.draftCompletionCache = {};
    }
    _state.draftCompletionCache[conversationId] = {
      draft,
      generationId,
      completedAt: Date.now(),
    };
  },

  // Pulisce la cache delle bozze per una conversazione
  ['CLEAR_DRAFT_COMPLETION_CACHE'](_state, conversationId) {
    if (
      _state.draftCompletionCache &&
      _state.draftCompletionCache[conversationId]
    ) {
      delete _state.draftCompletionCache[conversationId];
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
